/**
 * i18n — typy + lista wspieranych locale.
 *
 * **Routing strategy: always-prefix.** Każdy URL CMS zaczyna się od locale
 * (`/pl`, `/pl/o-nas`, `/en/about-us`). Brak locale w URL -> server redirect
 * na `/{DEFAULT_LOCALE}` (`/pl`).
 *
 * **Per-language slugs:** podstrony mają RÓŻNE slug'i per locale (np.
 * `/pl/o-nas` vs `/en/about-us`) — registry w `lib/catalogs/data.ts` ma
 * `slugs: Record<Locale, string>` per catalog.
 *
 * @see ../catalogs/data.ts — multi-language catalogs
 * @see ../../app/[locale]/ — routing structure
 */

export type Locale = "pl" | "en" | "de";

/** Lista wszystkich wspieranych locale — używane do walidacji + iteracji. */
export const LOCALES: Locale[] = ["pl", "en", "de"];

/** Domyślny locale gdy URL bez prefix'u — `/` redirect na `/pl`. */
export const DEFAULT_LOCALE: Locale = "pl";

/** PL labels dla switcher'a — wyświetlane userowi (NIE locale code). */
export const LOCALE_NAMES: Record<Locale, string> = {
  pl: "Polski",
  en: "English",
  de: "Deutsch",
};

/** Krótkie 2-letter labels (do compact UI np. WLanguageSwitcher). */
export const LOCALE_SHORT: Record<Locale, string> = {
  pl: "PL",
  en: "EN",
  de: "DE",
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as string[]).includes(value);
}

/**
 * Wyłuskuje locale z pathname (np. "/pl/o-nas" -> "pl").
 * Fallback do DEFAULT_LOCALE gdy pathname nie ma valid locale prefix'u.
 */
export function extractLocaleFromPath(pathname: string): Locale {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  return isLocale(first) ? first : DEFAULT_LOCALE;
}

/**
 * Strip locale prefix z pathname (np. "/pl/o-nas" -> "/o-nas", "/pl" -> "/").
 * Używane do uzyskania "logical path" niezależnego od locale.
 */
export function stripLocaleFromPath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0 && isLocale(segments[0])) {
    segments.shift();
  }
  return "/" + segments.join("/");
}
